import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Menu, Settings, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import AgentExperience from '../components/agent/AgentExperience';
import { AvatarProfile, useAdminApi } from '../hooks/useAdminApi';
import CustomerProfilePanel, {
  type BackendProfileSections,
  type ScrapedPage,
} from '../components/customer/CustomerProfilePanel';
import { type CtaEntry } from '../components/customer/CtaTable';
import {
  DEFAULT_PUBLIC_AGENT_SLUG,
  DEFAULT_PUBLIC_WORKSPACE_SLUG,
  sanitizePublicSlug,
} from '../utils/agentRouting';
import { buildCanonicalAgentUrl, requiresAvatarProfileResolution } from '../utils/avatarProfiles';
import {
  type CustomizeFlowInputs,
  type WorkspaceAppearanceMode,
  persistWorkspaceAppearanceMode,
  persistWorkspaceSlug,
  readWorkspaceAppearanceMode,
  readCustomizeFlowInputsFromStorage,
  sanitizeWorkspaceSlug,
  setupOrAdminAuthHeaders,
} from '../utils/workspace';

interface CustomerWorkspacePageProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
  agentRoutePresentation?: boolean;
  routeCustomerSlug?: string;
  routeWorkspaceSlug?: string;
  routeAgentSlug?: string;
}

interface WorkspaceLocationState {
  customizeFlow?: Partial<CustomizeFlowInputs>;
  workspaceSlug?: string;
}

interface WorkspaceProfile {
  workspaceSlug: string;
  website: string;
  productWebsite: string;
  lastScrapedAt: string | null;
  profileSections: BackendProfileSections;
  effectiveAgentName: string;
}

interface PublicAgentConfig {
  customerSlug: string;
  workspaceSlug: string;
  agentSlug: string;
  displayName: string;
  companyName: string;
  greeting: string;
  branding: {
    logoUrl?: string;
    iconUrl?: string;
    titleFontFamily?: string;
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
  };
}

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

const asTrimmedString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const HEX_COLOR_PATTERN = /#[0-9a-fA-F]{3,8}\b/g;
const RGB_COLOR_PATTERN = /rgba?\([^)]+\)/g;

const extractColorTokens = (value: string): string[] => {
  const matches = value.match(HEX_COLOR_PATTERN) ?? [];
  const rgbMatches = value.match(RGB_COLOR_PATTERN) ?? [];
  return [...matches, ...rgbMatches];
};

const isNeutralColor = (color: string): boolean => {
  const normalized = color.toLowerCase();
  return ['#fff', '#fefefe', '#fcfcfc', '#fafafa', '#f9f9f9', '#f5f5f5', '#f4f4f4', '#efefef', '#eee', '#ddd', '#ccc', '#bbb', '#aaa', '#999', '#888', '#777', '#666', '#555', '#444', '#333', '#222', '#111', '#000'].some((token) => normalized.startsWith(token));
};

const buildWorkspaceBackgroundStyle = (
  colorDescription: string,
  appearanceMode: WorkspaceAppearanceMode,
): React.CSSProperties | undefined => {
  if (appearanceMode !== 'brand') {
    return undefined;
  }

  const tokens = extractColorTokens(colorDescription);
  if (tokens.length === 0) {
    return undefined;
  }

  const primary = tokens.find((token) => !isNeutralColor(token)) ?? tokens[0];
  const secondary = tokens.find((token) => token !== primary && !isNeutralColor(token)) ?? tokens[1] ?? primary;
  const neutral = tokens.find((token) => isNeutralColor(token)) ?? 'rgba(255,255,255,0.94)';

  return {
    backgroundImage: `radial-gradient(circle at top left, ${primary}22 0%, transparent 38%), radial-gradient(circle at top right, ${secondary}18 0%, transparent 34%), linear-gradient(180deg, ${neutral} 0%, rgba(255,255,255,0.72) 100%)`,
    backgroundColor: neutral,
  };
};

const emptyBackendProfileSections = (): BackendProfileSections => ({
  agent_name: '',
  company_name: '',
  founding_year_and_location: '',
  founders: '',
  contact_details: '',
  industry: '',
  mission_vision_values: '',
  positioning_core_message: '',
  achievements_milestones: '',
  customers_target_groups: '',
  calls_to_action: '',
  products_services: '',
  address_formality: '',
  language_style: '',
  emoji_usage: '',
  recurring_phrases: '',
  landing_page_excerpt: '',
  color_codes: '',
});

const normalizeProfileSectionsResponse = (payload: unknown): BackendProfileSections => {
  if (!payload || typeof payload !== 'object') {
    return emptyBackendProfileSections();
  }

  const source = payload as Record<string, unknown>;
  return {
    agent_name: asTrimmedString(source.agent_name),
    company_name: asTrimmedString(source.company_name),
    founding_year_and_location: asTrimmedString(source.founding_year_and_location),
    founders: asTrimmedString(source.founders),
    contact_details: asTrimmedString(source.contact_details),
    industry: asTrimmedString(source.industry),
    mission_vision_values: asTrimmedString(source.mission_vision_values),
    positioning_core_message: asTrimmedString(source.positioning_core_message),
    achievements_milestones: asTrimmedString(source.achievements_milestones),
    customers_target_groups: asTrimmedString(source.customers_target_groups),
    calls_to_action: asTrimmedString(source.calls_to_action),
    products_services: asTrimmedString(source.products_services),
    address_formality: asTrimmedString(source.address_formality),
    language_style: asTrimmedString(source.language_style),
    emoji_usage: asTrimmedString(source.emoji_usage),
    recurring_phrases: asTrimmedString(source.recurring_phrases),
    landing_page_excerpt: asTrimmedString(source.landing_page_excerpt),
    color_codes: asTrimmedString(source.color_codes),
  };
};

const hasProfileSectionContent = (sections: BackendProfileSections): boolean => (
  Object.values(sections).some((value) => value.trim().length > 0)
);

const normalizeProfileResponse = (payload: unknown, fallbackSlug: string): WorkspaceProfile | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const data = payload as Record<string, unknown>;
  return {
    workspaceSlug: sanitizeWorkspaceSlug(asTrimmedString(data.workspaceSlug) || fallbackSlug),
    website: asTrimmedString(data.website),
    productWebsite: asTrimmedString(data.productWebsite),
    lastScrapedAt: typeof data.lastScrapedAt === 'string' ? data.lastScrapedAt : null,
    profileSections: normalizeProfileSectionsResponse(data.profile_sections),
    effectiveAgentName: asTrimmedString(data.effectiveAgentName),
  };
};

const normalizePublicAgentConfig = (
  payload: unknown,
  fallbackCustomerSlug: string,
  fallbackWorkspaceSlug: string,
  fallbackAgentSlug: string,
): PublicAgentConfig | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const data = payload as Record<string, unknown>;
  const brandingRaw = data.branding && typeof data.branding === 'object'
    ? (data.branding as Record<string, unknown>)
    : {};

  return {
    customerSlug: sanitizePublicSlug(asTrimmedString(data.customerSlug) || asTrimmedString(data.customer_slug), fallbackCustomerSlug),
    workspaceSlug: sanitizePublicSlug(asTrimmedString(data.workspaceSlug) || asTrimmedString(data.workspace_slug), fallbackWorkspaceSlug),
    agentSlug: sanitizePublicSlug(asTrimmedString(data.agentSlug) || asTrimmedString(data.agent_slug), fallbackAgentSlug),
    displayName: asTrimmedString(data.displayName) || asTrimmedString(data.display_name),
    companyName: asTrimmedString(data.companyName) || asTrimmedString(data.company_name),
    greeting: asTrimmedString(data.greeting),
    branding: {
      logoUrl: asTrimmedString(brandingRaw.logoUrl) || asTrimmedString(brandingRaw.logo_url) || undefined,
      iconUrl: asTrimmedString(brandingRaw.iconUrl) || asTrimmedString(brandingRaw.icon_url) || undefined,
      titleFontFamily: asTrimmedString(brandingRaw.titleFontFamily) || asTrimmedString(brandingRaw.title_font_family) || undefined,
      primaryColor: asTrimmedString(brandingRaw.primaryColor) || asTrimmedString(brandingRaw.primary_color) || undefined,
      secondaryColor: asTrimmedString(brandingRaw.secondaryColor) || asTrimmedString(brandingRaw.secondary_color) || undefined,
      backgroundColor: asTrimmedString(brandingRaw.backgroundColor) || asTrimmedString(brandingRaw.background_color) || undefined,
    },
  };
};

const extractErrorMessage = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const data = payload as Record<string, unknown>;
  return typeof data.error === 'string' && data.error.trim().length > 0 ? data.error : null;
};

const normalizeScrapedPagesResponse = (payload: unknown): ScrapedPage[] => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const data = payload as Record<string, unknown>;
  const pagesRaw = Array.isArray(data.scrapedPages) ? data.scrapedPages : [];

  return pagesRaw.map((entry) => {
    const row = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {};
    return {
      url: asTrimmedString(row.url),
      sourceType: asTrimmedString(row.sourceType) || 'website',
      title: asTrimmedString(row.title),
      metaDescription: asTrimmedString(row.metaDescription),
      extractedText: asTrimmedString(row.extractedText),
      scrapedAt: asTrimmedString(row.scrapedAt) || undefined,
      faviconUrl: asTrimmedString(row.faviconUrl) || undefined,
      logoUrl: asTrimmedString(row.logoUrl) || undefined,
      titleFontFamily: asTrimmedString(row.titleFontFamily) || undefined,
      titleColor: asTrimmedString(row.titleColor) || undefined,
    };
  });
};

const CustomerWorkspacePage: React.FC<CustomerWorkspacePageProps> = ({
  isDarkMode,
  onToggleTheme,
  agentRoutePresentation = false,
  routeCustomerSlug,
  routeWorkspaceSlug,
  routeAgentSlug,
}) => {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, username: adminUsername, adminSession, getAdminProfile, getCustomerAvatars } = useAdminApi();
  const locationState = (location.state as WorkspaceLocationState | null) ?? null;
  const [adminEmail, setAdminEmail] = useState<string | null>(null);
  const [backendProfile, setBackendProfile] = useState<WorkspaceProfile | null>(null);
  const [agentConfig, setAgentConfig] = useState<PublicAgentConfig | null>(null);
  const [scrapedPages, setScrapedPages] = useState<ScrapedPage[]>([]);
  const [ctas, setCtas] = useState<CtaEntry[]>([]);
  const [selectedAvatarProfile, setSelectedAvatarProfile] = useState<AvatarProfile | null>(null);
  const [avatarProfileLoading, setAvatarProfileLoading] = useState(() => (
    requiresAvatarProfileResolution(routeAgentSlug, location.search)
  ));
  const [avatarProfileError, setAvatarProfileError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [scrapedError, setScrapedError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [chatKey, setChatKey] = useState(0);
  const initialCustomerSlug = sanitizeWorkspaceSlug(routeCustomerSlug || slugParam || locationState?.workspaceSlug);
  const [appearanceMode, setAppearanceMode] = useState<WorkspaceAppearanceMode>(() => readWorkspaceAppearanceMode(initialCustomerSlug));

  const storedCustomizeFlow = useMemo(() => readCustomizeFlowInputsFromStorage(), []);
  const workspaceSlug = initialCustomerSlug;
  const workspaceScopeSlug = sanitizePublicSlug(routeWorkspaceSlug, DEFAULT_PUBLIC_WORKSPACE_SLUG);
  const agentSlug = sanitizePublicSlug(routeAgentSlug, DEFAULT_PUBLIC_AGENT_SLUG);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedAvatarSlug = searchParams.has('avatar')
    ? sanitizePublicSlug(searchParams.get('avatar'), '')
    : '';
  const targetAgentSlug = agentSlug === 'sally' && requestedAvatarSlug && requestedAvatarSlug !== 'sally'
    ? requestedAvatarSlug
    : agentSlug;
  const selectedAvatarSlug = selectedAvatarProfile?.agent_slug || selectedAvatarProfile?.slug || '';
  const avatarProfileReady = targetAgentSlug === 'sally'
    || (!avatarProfileLoading && selectedAvatarSlug === targetAgentSlug);
  const isPublicDemo = searchParams.get('public') === 'demo';
  const customerMemberships = Array.isArray(adminSession?.customer_memberships)
    ? (adminSession?.customer_memberships || [])
    : [];
  const isEndCustomer = adminSession?.role === 'customeruser'
    || (
      customerMemberships.some((membership) => membership.role === 'customeruser')
      && !customerMemberships.some((membership) => membership.role === 'customeradmin')
    );
  const publicDemoExitUrl = isPublicDemo ? '/' : undefined;
  const fallbackWebsite = (locationState?.customizeFlow?.website ?? storedCustomizeFlow.website ?? '').trim();
  const fallbackProductWebsite = (locationState?.customizeFlow?.productWebsite ?? storedCustomizeFlow.productWebsite ?? '').trim();

  useEffect(() => {
    persistWorkspaceSlug(workspaceSlug);
  }, [workspaceSlug]);

  useEffect(() => {
    if (!isAuthenticated || isEndCustomer) return;
    getAdminProfile().then(p => { if (p) setAdminEmail(p.email ?? null); });
  }, [isAuthenticated, isEndCustomer, getAdminProfile]);

  useEffect(() => {
    setAppearanceMode(readWorkspaceAppearanceMode(workspaceSlug));
  }, [workspaceSlug]);

  useEffect(() => {
    let cancelled = false;

    const resolveSelectedAvatar = async () => {
      if (targetAgentSlug === 'sally') {
        setSelectedAvatarProfile(null);
        setAvatarProfileError(null);
        setAvatarProfileLoading(false);
        return;
      }
      if (!isAuthenticated) {
        setSelectedAvatarProfile(null);
        setAvatarProfileError('Dieser Avatar ist für den aktuellen Zugang nicht verfügbar. Sally wird verwendet.');
        setAvatarProfileLoading(false);
        return;
      }

      setAvatarProfileLoading(true);
      setAvatarProfileError(null);
      const profiles = await getCustomerAvatars(workspaceSlug);
      if (cancelled) return;
      const selected = profiles?.find(profile => (
        (profile.agent_slug || profile.slug) === targetAgentSlug && profile.status === 'published'
      )) || null;
      setSelectedAvatarProfile(selected);
      setAvatarProfileError(selected ? null : 'Dieser Agent ist für den Kunden nicht freigeschaltet oder deaktiviert.');
      setAvatarProfileLoading(false);
      if (selected && agentSlug === 'sally' && requestedAvatarSlug && requestedAvatarSlug !== 'sally') {
        navigate(
          buildCanonicalAgentUrl(workspaceSlug, selected.agent_slug || selected.slug, location.search),
          { replace: true },
        );
      }
    };

    void resolveSelectedAvatar();
    return () => { cancelled = true; };
  }, [agentSlug, getCustomerAvatars, isAuthenticated, location.search, navigate, requestedAvatarSlug, targetAgentSlug, workspaceSlug]);

  useEffect(() => {
    let isMounted = true;

    const loadWorkspaceData = async () => {
      setIsLoading(true);
      setProfileError(null);
      setScrapedError(null);

      const agentConfigUrl = `${API_BASE}/api/public/agents/${encodeURIComponent(workspaceSlug)}/${encodeURIComponent(agentSlug)}/config`;
      const profileUrl = `${API_BASE}/api/customer/workspace/${encodeURIComponent(workspaceSlug)}`;
      const scrapedContentUrl = `${API_BASE}/api/customer/workspace/${encodeURIComponent(workspaceSlug)}/scraped-content`;
      const ctasUrl = `${API_BASE}/api/customer/workspace/${encodeURIComponent(workspaceSlug)}/ctas?agent=${encodeURIComponent(agentSlug)}`;
      const scopedHeaders = setupOrAdminAuthHeaders(workspaceSlug);
      const hasScopedAuthorization = Boolean(scopedHeaders.Authorization);

      const [agentConfigResult, profileResult, scrapedResult, ctasResult] = await Promise.allSettled([
        fetch(agentConfigUrl, { headers: scopedHeaders }),
        fetch(profileUrl, { headers: scopedHeaders }),
        fetch(scrapedContentUrl, { headers: scopedHeaders }),
        fetch(ctasUrl, { headers: scopedHeaders }),
      ]);

      if (!isMounted) {
        return;
      }

      let nextProfile: WorkspaceProfile | null = null;
      let nextAgentConfig: PublicAgentConfig | null = null;
      let nextProfileError: string | null = null;
      let nextScrapedPages: ScrapedPage[] = [];
      let nextScrapedError: string | null = null;
      let nextCtas: CtaEntry[] = [];

      if (agentConfigResult.status === 'fulfilled' && agentConfigResult.value.ok) {
        try {
          const configJson = await agentConfigResult.value.json();
          if (!isMounted) {
            return;
          }
          nextAgentConfig = normalizePublicAgentConfig(
            configJson,
            workspaceSlug,
            workspaceScopeSlug,
            agentSlug,
          );
        } catch {
          nextAgentConfig = null;
        }
      } else if (
        agentSlug !== DEFAULT_PUBLIC_AGENT_SLUG
        && agentConfigResult.status === 'fulfilled'
        && (agentConfigResult.value.status === 401 || agentConfigResult.value.status === 403)
      ) {
        const redirect = `${location.pathname}${location.search}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
        return;
      }

      if (profileResult.status === 'fulfilled') {
        if (profileResult.value.ok) {
          try {
            const profileJson = await profileResult.value.json();
            if (!isMounted) {
              return;
            }
            const normalizedProfile = normalizeProfileResponse(profileJson, workspaceSlug);
            if (normalizedProfile) {
              nextProfile = normalizedProfile;
            } else {
              nextProfileError = 'Kundenprofil konnte nicht verarbeitet werden.';
            }
          } catch {
            nextProfileError = 'Kundenprofil konnte nicht verarbeitet werden.';
          }
        } else if (profileResult.value.status === 404) {
          nextProfileError = 'Für diesen Workspace wurde noch kein Profil gespeichert.';
        } else if (
          !hasScopedAuthorization
          && (profileResult.value.status === 401 || profileResult.value.status === 403)
        ) {
          nextProfileError = null;
        } else {
          nextProfileError = `Kundenprofil konnte nicht geladen werden (${profileResult.value.status}).`;
        }
      } else {
        nextProfileError = 'Kundenprofil konnte nicht geladen werden.';
      }

      if (scrapedResult.status === 'fulfilled') {
        if (scrapedResult.value.ok) {
          try {
            const scrapedJson = await scrapedResult.value.json();
            if (!isMounted) {
              return;
            }
            nextScrapedPages = normalizeScrapedPagesResponse(scrapedJson);
          } catch {
            nextScrapedError = 'Gescrapte Inhalte konnten nicht verarbeitet werden.';
          }
        } else if (scrapedResult.value.status !== 404) {
          if (
            hasScopedAuthorization
            || (scrapedResult.value.status !== 401 && scrapedResult.value.status !== 403)
          ) {
            nextScrapedError = `Gescrapte Inhalte konnten nicht geladen werden (${scrapedResult.value.status}).`;
          }
        }
      } else {
        nextScrapedError = 'Gescrapte Inhalte konnten nicht geladen werden.';
      }

      if (ctasResult.status === 'fulfilled' && ctasResult.value.ok) {
        try {
          const ctasJson = await ctasResult.value.json();
          if (isMounted && Array.isArray(ctasJson)) {
            nextCtas = ctasJson as CtaEntry[];
          }
        } catch {
          // CTAs are non-critical; ignore parse errors
        }
      }

      if (
        nextProfile &&
        !hasProfileSectionContent(nextProfile.profileSections) &&
        nextScrapedPages.length > 0
      ) {
        try {
          const refreshResponse = await fetch(profileUrl, {
            method: 'PATCH',
            headers: {
              ...scopedHeaders,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              refresh_from_scraped: true,
              use_llm: true,
            }),
          });

          if (refreshResponse.ok) {
            const refreshedProfileJson = await refreshResponse.json();
            if (isMounted) {
              const refreshedProfile = normalizeProfileResponse(refreshedProfileJson, workspaceSlug);
              if (refreshedProfile) {
                nextProfile = refreshedProfile;
                nextProfileError = null;
              }
            }
          }
        } catch {
          // Keep original UI state if refresh fails.
        }
      }

      if (!isMounted) {
        return;
      }

      setBackendProfile(nextProfile);
      setAgentConfig(nextAgentConfig);
      setProfileError(nextProfileError);
      setScrapedPages(nextScrapedPages);
      setScrapedError(nextScrapedError);
      setCtas(nextCtas);
      setIsLoading(false);
    };

    void loadWorkspaceData();

    return () => {
      isMounted = false;
    };
  }, [agentSlug, location.pathname, location.search, navigate, workspaceScopeSlug, workspaceSlug]);

  const effectiveProfile = backendProfile ?? {
    workspaceSlug,
    website: fallbackWebsite,
    productWebsite: fallbackProductWebsite,
    lastScrapedAt: null,
    profileSections: emptyBackendProfileSections(),
    effectiveAgentName: '',
  };

  const primaryScrapedPage = scrapedPages.find((page) => page.sourceType === 'website') ?? scrapedPages[0];
  const agentName = agentConfig?.displayName || effectiveProfile.effectiveAgentName || effectiveProfile.profileSections.agent_name.trim() || 'Sally';
  const effectiveAvatarName = selectedAvatarProfile?.name || agentName;
  const avatarProfileSlug = agentSlug === 'sally' && requestedAvatarSlug && requestedAvatarSlug !== 'sally'
    ? selectedAvatarProfile?.slug
    : undefined;
  const companyName = agentConfig?.companyName || effectiveProfile.profileSections.company_name || effectiveProfile.workspaceSlug;
  const brandLogoUrl = agentConfig?.branding.logoUrl || primaryScrapedPage?.logoUrl || primaryScrapedPage?.faviconUrl;
  const brandIconUrl = agentConfig?.branding.iconUrl || primaryScrapedPage?.faviconUrl || primaryScrapedPage?.logoUrl;
  const titleFontFamily = agentConfig?.branding.titleFontFamily || primaryScrapedPage?.titleFontFamily;
  const configuredColorDescription = [
    agentConfig?.branding.primaryColor,
    agentConfig?.branding.secondaryColor,
    agentConfig?.branding.backgroundColor,
  ].filter(Boolean).join(' ');
  const brandBackgroundStyle = buildWorkspaceBackgroundStyle(
    configuredColorDescription || effectiveProfile.profileSections.color_codes,
    appearanceMode,
  );
  const isSuperadmin = Boolean(adminSession?.is_superadmin)
    || adminSession?.role === 'superadmin'
    || adminSession?.platform_role === 'owner';
  const showWorkspaceControls = !agentRoutePresentation && !isPublicDemo && isAuthenticated && isSuperadmin;

  useEffect(() => {
    const title = `${companyName} - Conversation Agent`;
    const previousTitle = document.title;
    document.title = title;

    const faviconElement = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    const previousFaviconHref = faviconElement?.href ?? null;
    if (faviconElement && brandIconUrl) {
      faviconElement.href = brandIconUrl;
    }

    return () => {
      document.title = previousTitle;
      if (faviconElement && previousFaviconHref) {
        faviconElement.href = previousFaviconHref;
      }
    };
  }, [companyName, brandIconUrl]);

  const saveProfileSections = async (sections: BackendProfileSections): Promise<void> => {
    const profileUrl = `${API_BASE}/api/customer/workspace/${encodeURIComponent(workspaceSlug)}`;
    const response = await fetch(profileUrl, {
      method: 'PATCH',
      headers: setupOrAdminAuthHeaders(workspaceSlug),
      body: JSON.stringify({
        profile_sections: sections,
      }),
    });

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      throw new Error(extractErrorMessage(payload) || `save_failed_${response.status}`);
    }

    const normalized = normalizeProfileResponse(payload, workspaceSlug);
    if (!normalized) {
      throw new Error('save_failed_invalid_profile');
    }

    setBackendProfile(normalized);
  };

  const saveCtas = async (updatedCtas: CtaEntry[]): Promise<void> => {
    const ctasUrl = `${API_BASE}/api/customer/workspace/${encodeURIComponent(workspaceSlug)}/ctas?agent=${encodeURIComponent(agentSlug)}`;
    const response = await fetch(ctasUrl, {
      method: 'PUT',
      headers: setupOrAdminAuthHeaders(workspaceSlug),
      body: JSON.stringify(updatedCtas),
    });
    if (!response.ok) {
      throw new Error(`save_ctas_failed_${response.status}`);
    }
    setCtas(updatedCtas);
  };

  const startNewChat = (): void => {
    setChatKey((current) => current + 1);
    setSidebarOpen(false);
  };

  const handleChangeAppearanceMode = (mode: WorkspaceAppearanceMode): void => {
    const persistedMode = persistWorkspaceAppearanceMode(workspaceSlug, mode);
    setAppearanceMode(persistedMode);
  };

  const buttonClasses = isDarkMode
    ? 'hover:bg-gray-700 text-gray-300'
    : 'hover:bg-gray-100 text-gray-600';
  const statusCardClasses = isDarkMode
    ? 'border border-slate-700/80 bg-slate-900/92 text-slate-100'
    : 'border border-gray-200 bg-white/92 text-slate-800';
  const settingsButtonClasses = isDarkMode
    ? 'bg-gray-800/95 text-gray-200 hover:bg-gray-700'
    : 'bg-white/95 text-gray-700 hover:bg-gray-100';
  const mutedClasses = isDarkMode ? 'text-slate-300' : 'text-slate-600';

  return (
    <div className="flex h-screen overflow-hidden">
      {!agentRoutePresentation && !isPublicDemo && isAuthenticated && !isEndCustomer && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isDarkMode={isDarkMode}
          onToggleTheme={onToggleTheme}
          onStartNewChat={startNewChat}
          onOpenSettings={() => setIsSettingsOpen(true)}
          settingsLabel="Workspace"
          brandName={companyName}
          brandIconUrl={brandIconUrl}
          adminUsername={adminUsername}
          adminEmail={adminEmail}
        />
      )}

      <div className="relative flex-1 min-w-0">
        {avatarProfileReady ? (
          <AgentExperience
            key={`${chatKey}:${agentSlug}:${avatarProfileSlug || 'direct'}`}
            isDarkMode={isDarkMode}
            onToggleTheme={onToggleTheme}
            workspaceSlug={effectiveProfile.workspaceSlug}
            agentSlug={agentSlug}
            avatarProfileSlug={avatarProfileSlug}
            companyName={companyName}
            agentName={effectiveAvatarName}
            titleFontFamily={titleFontFamily}
            brandLogoUrl={brandLogoUrl}
            brandIconUrl={brandIconUrl}
            brandBackgroundStyle={brandBackgroundStyle}
            dashboardUrl={agentRoutePresentation || isPublicDemo || isEndCustomer ? undefined : `/dashboard/${effectiveProfile.workspaceSlug}`}
            publicExitUrl={publicDemoExitUrl}
          />
        ) : (
          <div className={`flex h-full items-center justify-center ${isDarkMode ? 'bg-slate-950 text-slate-300' : 'bg-slate-50 text-slate-600'}`} role="status">
            Avatar wird vorbereitet…
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between p-4">
          <div className="pointer-events-auto flex items-center gap-2">
            {isPublicDemo ? (
              <a
                href="/"
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition-colors ${
                  isDarkMode
                    ? 'bg-slate-900/90 text-slate-100 hover:bg-slate-800'
                    : 'bg-white/95 text-slate-700 hover:bg-slate-100'
                }`}
                title="Zurück zu Synaworks.ai"
              >
                <ChevronLeft className="h-4 w-4" />
                Synaworks.ai
              </a>
            ) : !agentRoutePresentation && !isEndCustomer ? (
              <Link
                to={`/dashboard/${effectiveProfile.workspaceSlug}`}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition-colors ${
                  isDarkMode
                    ? 'bg-slate-900/90 text-slate-100 hover:bg-slate-800'
                    : 'bg-white/95 text-slate-700 hover:bg-slate-100'
                }`}
                title="Zurück zum Dashboard"
              >
                <ChevronLeft className="h-4 w-4" />
                Dashboard
              </Link>
            ) : null}
            {!agentRoutePresentation && !isPublicDemo && isAuthenticated && !isEndCustomer && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className={`rounded-lg p-2.5 transition-colors ${buttonClasses}`}
              >
                <Menu size={20} />
              </button>
            )}
          </div>

          <div className="pointer-events-auto ml-auto flex items-start gap-3">
            {(isLoading || profileError || scrapedError || avatarProfileError) ? (
              <div className={`max-w-md rounded-2xl px-4 py-3 text-sm shadow-[0_18px_45px_rgba(15,23,42,0.12)] ${statusCardClasses}`}>
                {isLoading ? (
                  <p className="font-medium">{agentRoutePresentation ? 'Agent wird geladen...' : 'Workspace-Daten werden geladen...'}</p>
                ) : null}
                {profileError ? (
                  <p className={isLoading ? 'mt-2' : ''}>{profileError}</p>
                ) : null}
                {scrapedError ? (
                  <p className={isLoading || profileError ? 'mt-2' : ''}>{scrapedError}</p>
                ) : null}
                {avatarProfileError ? (
                  <p className={isLoading || profileError || scrapedError ? 'mt-2' : ''}>{avatarProfileError}</p>
                ) : null}
              </div>
            ) : null}

            {showWorkspaceControls ? (
              <button
                type="button"
                onClick={() => setIsSettingsOpen(true)}
                className={`pointer-events-auto rounded-lg p-2.5 transition-colors ${settingsButtonClasses}`}
              >
                <Settings className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>

        {isSettingsOpen && showWorkspaceControls ? (
          <>
            <div
              className="absolute inset-0 z-30 bg-slate-950/45 backdrop-blur-[1px]"
              onClick={() => setIsSettingsOpen(false)}
            />
            <aside className={`absolute right-0 top-0 z-40 h-full w-full max-w-2xl overflow-hidden shadow-[-18px_0_45px_rgba(15,23,42,0.18)] ${
              isDarkMode ? 'bg-slate-950' : 'bg-orange-50'
            }`}>
              <div className={`flex items-center justify-between border-b px-5 py-4 ${
                isDarkMode ? 'border-white/10 bg-slate-900 text-white' : 'border-orange-100 bg-white text-slate-900'
              }`}>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">Workspace Einstellungen</h2>
                  <p className={`mt-1 text-sm ${mutedClasses}`}>
                    Kategorien prüfen, anpassen und speichern.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSettingsOpen(false)}
                  className={`rounded-xl p-2 transition ${
                    isDarkMode ? 'text-slate-300 hover:bg-slate-800 hover:text-white' : 'text-slate-500 hover:bg-orange-50 hover:text-slate-900'
                  }`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="h-[calc(100%-5rem)] overflow-y-auto p-4 sm:p-5">
                <CustomerProfilePanel
                  workspaceSlug={effectiveProfile.workspaceSlug}
                  website={effectiveProfile.website}
                  productWebsite={effectiveProfile.productWebsite}
                  lastScrapedAt={effectiveProfile.lastScrapedAt}
                  initialProfileSections={effectiveProfile.profileSections}
                  scrapedPages={scrapedPages}
                  isLoading={isLoading}
                  scrapedError={scrapedError}
                  onSaveSections={saveProfileSections}
                  ctas={ctas}
                  onSaveCtas={saveCtas}
                  appearanceMode={appearanceMode}
                  onChangeAppearanceMode={handleChangeAppearanceMode}
                  isDarkMode={isDarkMode}
                />
              </div>
            </aside>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CustomerWorkspacePage;
